import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Clock,
  Euro,
  MapPin,
  ShieldCheck,
  Users,
} from "lucide-react";
import NotFound from "./NotFound";
import { extraescolarRoutes, getExtraescolarBySlug } from "../lib/extraescolares";

const accent = "#C9974A";
const dark = "#0D3A4F";
const light = "#FBF3E3";

const defaultPractical = [
  { label: "Duración", value: "Curso escolar completo, de septiembre a junio", icon: Clock },
  { label: "Horario", value: "Consultar disponibilidad", icon: Clock },
  { label: "Formato", value: "Grupo reducido", icon: Users },
  { label: "Lugar", value: "C/ del Poeta Rafael Morales 2, San Sebastián de los Reyes, Madrid", icon: MapPin },
];

const practicalIcons = {
  Precio: Euro,
  Fechas: CalendarDays,
  Horario: Clock,
  Formato: Users,
  Lugar: MapPin,
  Edades: Users,
  Ratio: Users,
  Duración: Clock,
  Frecuencia: Clock,
  "Horas semanales": Clock,
  Materiales: ShieldCheck,
  Requisitos: ShieldCheck,
  Herramientas: ShieldCheck,
  Hitos: ShieldCheck,
  Eventos: CalendarDays,
  Objetivos: ShieldCheck,
};

function getPracticalItems(activity) {
  if (!activity.practical?.length) {
    return defaultPractical;
  }

  return activity.practical.map((item) => ({
    ...item,
    icon: practicalIcons[item.label] || ShieldCheck,
  }));
}

export default function ExtraescolarDetalle() {
  const { slug } = useParams();
  const activity = getExtraescolarBySlug(slug);

  if (!activity) {
    return <NotFound />;
  }

  const practicalItems = getPracticalItems(activity);

  return (
    <main data-testid="extraescolar-detail-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="relative pt-24 pb-16 px-5 lg:px-8" style={{ background: light }}>
        <div className="max-w-7xl mx-auto">
          <Link
            to="/cursos-especializados"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-8"
            style={{ color: "#8B6020" }}
          >
            <ArrowLeft size={16} />
            Volver a extraescolares
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="section-label mb-5" style={{ color: accent }}>
                {activity.category} · {activity.level}
              </div>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ fontFamily: "Playfair Display, serif", color: dark, lineHeight: 1.12 }}
              >
                {activity.label}
              </h1>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">{activity.summary}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/cursos-especializados#solicitar-info"
                  state={{ areaInteres: activity.label }}
                  className="btn-primary"
                  style={{ background: accent }}
                >
                  Solicitar información <ArrowRight size={16} />
                </Link>
                <a
                  href="#contenido"
                  className="btn-outline"
                  style={{ color: "#8B6020", borderColor: accent }}
                >
                  Ver contenido
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="overflow-hidden shadow-xl"
              style={{ borderRadius: "8px", height: "430px" }}
            >
              <img src={activity.image} alt={activity.label} className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section id="contenido" className="py-20 px-5 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <div className="section-label mb-4" style={{ color: accent }}>
              Contenido de la actividad
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6" style={{ color: dark }}>
              Qué se trabaja
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">{activity.description}</p>
            <div className="grid grid-cols-1 gap-4">
              {activity.content.map((item) => (
                <div
                  key={item}
                  className="flex gap-4 p-5 border border-[#EEDDB8] bg-[#FFF8EE]"
                  style={{ borderRadius: "8px" }}
                >
                  <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: accent }} />
                  <p className="text-sm leading-relaxed text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <aside>
            <div className="sticky top-28 space-y-5">
              <div className="p-6 border border-slate-200 bg-white shadow-sm" style={{ borderRadius: "8px" }}>
                <h3 className="text-xl font-semibold mb-5" style={{ color: dark }}>
                  Información general
                </h3>
                <div className="space-y-4">
                  {practicalItems.map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex gap-3">
                      <Icon size={18} className="mt-0.5 flex-shrink-0" style={{ color: accent }} />
                      <div>
                        <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{label}</p>
                        <p className="text-sm text-slate-700">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 text-white" style={{ background: "#5C3D11", borderRadius: "8px" }}>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={18} style={{ color: "#F4D59A" }} />
                  <h3 className="font-semibold">Plazas y grupos</h3>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#E8D5B5" }}>
                  Los grupos se cerrarán por nivel, edad y encaje de intereses. Si dudas entre iniciación y avanzado,
                  escríbenos y te orientamos.
                </p>
              </div>

              {activity.onlineNotice ? (
                <div
                  className="p-6 border-2"
                  style={{ background: "#FFF8EE", borderColor: "#C9974A", borderRadius: "8px" }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} style={{ color: "#C9974A" }} />
                    <h3 className="font-semibold" style={{ color: dark }}>{activity.onlineNotice.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700 mb-4">
                    {activity.onlineNotice.text}
                  </p>
                  <Link
                    to="/cursos-especializados#solicitar-info"
                    state={{
                      areaInteres: activity.onlineNotice.cta.includes("Programación online")
                          ? "Programación online"
                          : activity.label,
                      modalidadPreferida: activity.onlineNotice.cta.includes("online")
                        ? activity.onlineNotice.cta.replace("Preguntar por ", "")
                        : "",
                    }}
                    className="inline-flex items-center gap-2 text-sm font-semibold"
                    style={{ color: "#8B6020" }}
                  >
                    {activity.onlineNotice.cta} <ArrowRight size={14} />
                  </Link>
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </section>

      {activity.examples?.length ? (
        <section className="py-20 px-5 lg:px-8" style={{ background: light }}>
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="section-label mb-4" style={{ color: accent }}>
                {activity.examplesTitle || "Ejemplos"}
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-5" style={{ color: dark }}>
                {activity.examplesHeading || "Para imaginar la actividad"}
              </h2>
              {activity.examplesIntro ? (
                <p className="text-slate-600 leading-relaxed">{activity.examplesIntro}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {activity.examples.map((example, index) => (
                <div
                  key={example}
                  className="bg-white border border-[#EEDDB8] p-6"
                  style={{ borderRadius: "8px" }}
                >
                  <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>
                    Reto {String(index + 1).padStart(2, "0")}
                  </p>
                  <p className="text-sm leading-relaxed text-slate-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {activity.roadmap?.length ? (
        <section className="py-20 px-5 lg:px-8" style={{ background: light }}>
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="section-label mb-4" style={{ color: accent }}>
                Roadmap del curso
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-5" style={{ color: dark }}>
                Evolución esperada
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {activity.roadmapIntro ||
                  "Una progresión por bloques para que cada participante avance desde la observación y el control de la línea hasta una pieza personal donde combine las técnicas trabajadas."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {activity.roadmap.map((step, index) => (
                <div
                  key={`${step.period}-${step.title}`}
                  className="relative bg-white border border-[#EEDDB8] p-6"
                  style={{ borderRadius: "8px" }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-9 h-9 flex items-center justify-center text-sm font-bold text-white"
                      style={{ background: accent, borderRadius: "8px" }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{step.period}</p>
                      <h3 className="font-semibold" style={{ color: dark }}>{step.title}</h3>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-20 px-5 lg:px-8" style={{ background: activity.roadmap?.length || activity.examples?.length ? "#FFFFFF" : light }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="section-label mb-4" style={{ color: accent }}>
                Objetivos
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6" style={{ color: dark }}>
                Qué se llevan
              </h2>
              <div className="space-y-4">
                {activity.outcomes.map((outcome) => (
                  <div key={outcome} className="flex gap-3">
                    <Check size={18} className="mt-1 flex-shrink-0" style={{ color: accent }} />
                    <p className="text-slate-700 leading-relaxed">{outcome}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-7 bg-white border border-[#EEDDB8]" style={{ borderRadius: "8px" }}>
              <h3 className="text-xl font-semibold mb-4" style={{ color: dark }}>
                Otras extraescolares
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {extraescolarRoutes
                  .filter((item) => item.slug !== activity.slug)
                  .map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-[#FFF8EE] hover:text-[#8B6020] transition-colors"
                      style={{ borderRadius: "8px" }}
                    >
                      {item.shortLabel}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
